import React, { Fragment, memo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { confirmableRevokeUserTokens, resendOnboardingEmail } from '@actions/auth-actions';
import { showConfirmationDialog } from '@actions/confirmation-actions';
import {
  confirmableDeactivateDashboardEntry,
  confirmableReactivateDashboardEntry
} from '@actions/dashboard-actions';
import { colors } from '@constants/colors';
import BlockIcon from '@mui/icons-material/Block';
import LogoutIcon from '@mui/icons-material/Logout';
import SendIcon from '@mui/icons-material/Send';
import UndoIcon from '@mui/icons-material/Undo';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import IconMenu from '@shared/icon-menu';
import IconMenuItem from '@shared/icon-menu-item';
import { isAdmin } from '@utils/permission-utils';
import ApiTokensDialog from './api-tokens-dialog';

const UserActions = () => {
  const dispatch = useDispatch();
  const [apiTokensOpen, setApiTokensOpen] = useState(false);
  const {
    is_active: isActive,
    is_api_user: isApiUser,
    id
  } = useSelector(state => state.dashboard.details?.user?.data || {});

  const deactivate = () => {
    dispatch(showConfirmationDialog(
      'Deactivate user',
      'Are you sure you want to deactivate this user?' +
      ' This user will no longer be able to log in or perform actions until reactivated.',
      confirmableDeactivateDashboardEntry('user', Number(id))
    ));
  };

  const reactivate = () => {
    dispatch(showConfirmationDialog(
      'Reactivate user',
      'Are you sure you want to reactivate this user?' +
      ' This user will regain access to the application and perform actions.',
      confirmableReactivateDashboardEntry('user', Number(id))
    ));
  };

  const resendOnboarding = () => {
    dispatch(resendOnboardingEmail(id));
  };

  const revokeTokens = () => {
    dispatch(showConfirmationDialog(
      'Sign out of all sessions',
      'Are you sure you want to sign this user out of all their sessions?' +
      ' Their API tokens will also stop working.',
      confirmableRevokeUserTokens(Number(id))
    ));
  };

  // Only allow this functionality for admins:
  if (!isAdmin()) {
    return null;
  }

  const activeLabel = isActive ? <div style={{ color: colors.brand.red }}>Deactivate user</div> : 'Reactivate user';
  const activeCallback = isActive ? deactivate : reactivate;
  return (
    <Fragment>
      <IconMenu>
        {!isApiUser && <IconMenuItem icon={<SendIcon />} onClick={resendOnboarding}>Resend invite</IconMenuItem>}
        {isApiUser && isActive && (
          <IconMenuItem icon={<VpnKeyIcon />} onClick={() => setApiTokensOpen(true)}>API tokens</IconMenuItem>
        )}
        <IconMenuItem icon={<LogoutIcon />} onClick={revokeTokens}>Sign out of all sessions</IconMenuItem>
        <IconMenuItem icon={isActive ? <BlockIcon htmlColor={colors.brand.red} /> : <UndoIcon />}onClick={activeCallback}>
          {activeLabel}
        </IconMenuItem>
      </IconMenu>
      {apiTokensOpen && <ApiTokensDialog userId={id} onClose={() => setApiTokensOpen(false)} />}
    </Fragment>
  );
};

export default memo(UserActions);
